import { useEffect, useMemo, useRef, useState } from 'react';
import {
  detectShape,
  fromInput,
  inputType,
  toInput,
} from '@/components/dateShape';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Typography,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import UploadIcon from '@mui/icons-material/Upload';
import { useSnackbar } from 'notistack';
import {
  useExportItem,
  useItem,
  useCategories,
  useAudiences,
  useDeviceTypes,
  useShowTypes,
  useGenres,
  useRatings,
  useDeleteItem,
  useRestoreItem,
  useRevalidateItem,
  useUpdateItem,
  useReplaceAsset,
} from '@/api/queries';
import {
  EditorialInput,
  ItemProperties,
  ItemState,
  LocalityStringField,
  StringField,
  Validation,
  ValidationLevel,
} from '@/api/graphql';
import EditableField from '@/components/EditableField';
import type { EditableFieldProps } from '@/components/EditableField';
import { COUNTRIES, localityLabel } from '@/components/countries';
import { countByTab, findingsAtWorstLevel, tabForField } from './fieldTabs';
import EditableListField from '@/components/EditableListField';
import StateChip from '@/components/StateChip';
import ValidationChip from '@/components/ValidationChip';
import AssetThumbnail from '@/components/AssetThumbnail';
import RefreshButton from '@/components/RefreshButton';
import XmlTab from './XmlTab';
import { formatBytes, formatDateTime } from '@/utils';

/** Scalar fields the PoC lets an operator edit, grouped by tab. */
/**
 * How a field is edited. `vocabulary` offers the configured list, `countries` the ISO list; both render as
 * a dropdown, so a value outside the set cannot be typed — which is what round 2 asked for after
 * free-text fields accepted `Movieight`, `2023gfg` and `gdff`.
 */
type FieldKind =
  | { kind?: 'text' }
  | { kind: 'vocabulary'; vocabulary: 'showTypes' | 'audiences' }
  | { kind: 'countries' }
  | { kind: 'constrained'; constrain: EditableFieldProps['constrain'] };

type FieldDef = { name: keyof ItemProperties; label: string } & FieldKind;

const GENERAL: FieldDef[] = [
  { name: 'title', label: 'Title' },
  {
    name: 'titleBrief',
    label: 'Short title (max 19 chars)',
    kind: 'constrained',
    // The 19-character cap is a validation rule; maxLength stops an operator overshooting it silently.
    constrain: { maxLength: 19 },
  },
  { name: 'titleSortName', label: 'Sort title' },
  {
    name: 'showType',
    label: 'Show type',
    kind: 'vocabulary',
    vocabulary: 'showTypes',
  },
  {
    name: 'year',
    label: 'Year',
    kind: 'constrained',
    constrain: { inputMode: 'numeric', maxLength: 4, placeholder: 'YYYY' },
  },
  { name: 'countryOfOrigin', label: 'Country of origin', kind: 'countries' },
  {
    name: 'runTime',
    label: 'Run time (HH:MM:SS)',
    kind: 'constrained',
    constrain: { maxLength: 8, placeholder: 'HH:MM:SS' },
  },
  // HH:MM, not HH:MM:SS. Round 2 asked for seconds here, but all 14 real packages send HH:MM (00:43,
  // 01:27) and the rule matches that — requiring seconds would put an error on every item for data the
  // provider sends correctly. Flagged on VMS-52 rather than changed silently.
  {
    name: 'displayRunTime',
    label: 'Display run time (HH:MM)',
    kind: 'constrained',
    constrain: { maxLength: 5, placeholder: 'HH:MM' },
  },
  {
    name: 'audience',
    label: 'Audience',
    kind: 'vocabulary',
    vocabulary: 'audiences',
  },
  {
    name: 'hdContent',
    label: 'HD content (Y/N)',
    kind: 'constrained',
    constrain: { maxLength: 1, placeholder: 'Y' },
  },
  {
    name: 'audioType',
    label: 'Audio type',
  },
];

const DESCRIPTIONS: { name: keyof ItemProperties; label: string }[] = [
  { name: 'summaryShort', label: 'Short summary' },
  { name: 'summaryMedium', label: 'Medium summary' },
  { name: 'summaryLong', label: 'Long summary' },
  { name: 'episodeName', label: 'Episode name' },
];

const SERIES: { name: keyof ItemProperties; label: string }[] = [
  { name: 'seriesOrdinal', label: 'Season number' },
  { name: 'episodeOrdinal', label: 'Episode number' },
  { name: 'episodeId', label: 'Episode ID' },
];

const AVAILABILITY: FieldDef[] = [
  // "Should be a number, no text" — it is an ADI duration, so HH:MM:SS. Absent from all 14 real
  // packages, so a format rule here cannot reject anything the provider currently sends.
  {
    name: 'maximumViewingLength',
    label: 'Maximum viewing length (HH:MM:SS)',
    kind: 'constrained',
    constrain: { maxLength: 8, placeholder: 'HH:MM:SS' },
  },
  {
    name: 'suggestedPrice',
    label: 'Suggested price',
    kind: 'constrained',
    // A price, so digits and one decimal point.
    constrain: { inputMode: 'decimal', maxLength: 12 },
  },
];

/**
 * Tabs are addressed by id rather than by position, so code that jumps to one — revalidate landing
 * the operator on Validation — keeps working when the strip is reordered or a tab is added.
 */
type TabId =
  | 'general'
  | 'descriptions'
  | 'availability'
  | 'assets'
  | 'validation'
  | 'xml';

const ItemDetail = () => {
  const { assetId = '', version = '' } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { data: item, isLoading, error } = useItem(assetId, version);
  const { data: ratings } = useRatings();
  const { data: genres } = useGenres();
  const { data: categories } = useCategories();
  const { data: deviceTypes } = useDeviceTypes();
  const { data: showTypes } = useShowTypes();
  const { data: audiences } = useAudiences();
  const updateItem = useUpdateItem();
  const revalidate = useRevalidateItem();
  const exportItem = useExportItem();
  const deleteItem = useDeleteItem();
  const restoreItem = useRestoreItem();

  /**
   * Revalidate once whenever a different item (or version) is opened.
   *
   * Findings are *stored*, not computed on read — written at ingest and by this mutation. So a change
   * to a validation rule or a vocabulary leaves every existing item showing the findings it had when
   * it was last parsed, and nothing tells the operator the page is out of date. That is not
   * hypothetical: retiring one rule and recalibrating three others left all 14 items in the demo
   * environment displaying findings that no longer existed.
   *
   * Rules and vocabularies are configuration, edited far more often than items are re-ingested, so
   * opening an item is the natural moment to catch it up. Recomputing in `Query.item` instead is not
   * available: it is a DynamoDB resolver running under APPSYNC_JS, which cannot load config.
   *
   * The write is the point rather than a side effect — persisting also puts the fresh findings on the
   * table stream, so the catalogue and its facet counts heal too, not just this page.
   *
   * Guarded by a ref because the mutation invalidates the item query on success: without it, the
   * refetch would re-run the effect and loop. Silent by design — no snackbar and no tab jump, unlike
   * the Revalidate button, because the operator did not ask for this and the stored findings are
   * already on screen. A failure leaves those in place, which is the safe outcome.
   */
  const revalidatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!item) {
      return;
    }
    const key = `${item.assetId}@${item.version}`;
    if (revalidatedFor.current === key) {
      return;
    }
    revalidatedFor.current = key;
    // A different item has its own optimistic-lock counter; carrying the previous one over would make the
    // first save on this item fail.
    lastSavedVersion.current = null;
    setSaveError(null);
    revalidate.mutate({ assetId: item.assetId, version: item.version });
  }, [item, revalidate]);

  /**
   * Autosave.
   *
   * The explicit Save remains — it is the habit, and it gives an operator a way to force a write — but
   * an edit no longer depends on remembering it.
   *
   * Debounced on the whole pending draft rather than fired per field. Coalescing every dirty field into
   * one `updateItem` keeps a single write in flight, which matters because the mutation is optimistically
   * locked: two concurrent saves would make the second fail against a counter the first had already
   * moved. The delay is on change rather than on blur so a field left focused still saves.
   *
   * Worth recording that the version-churn worry this ticket was written around does not exist. Each
   * save increments `editVersion`, which is an optimistic-lock counter, not a version of the item — item
   * versions are separate rows keyed by ingest timestamp, which is what `itemVersions` and the revert
   * path read. Autosave therefore cannot pollute the version history.
   */
  const AUTOSAVE_DELAY_MS = 1200;
  const lastSavedVersion = useRef<number | null>(null);
  /**
   * The current `save`, so the autosave effect can be declared before this component's early returns.
   *
   * `save` closes over `item`, which only exists past the loading and error guards, so it cannot be
   * defined above them — and a hook declared below them is called conditionally, which is a rules-of-hooks
   * violation that breaks as soon as an item fails to load.
   */
  const saveRef = useRef<
    ((options?: { silent?: boolean }) => Promise<void>) | null
  >(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<TabId>('general');

  /**
   * Take the operator to the field a finding is about.
   *
   * "Show type is not one of the configured show types" names the field but not the place, so the
   * operator had to open every tab looking for it. This switches to the owning tab, then focuses and
   * scrolls to the input.
   *
   * The focus is deferred because the target tab has not rendered yet at the moment the tab state
   * changes — a `getElementById` in the same tick finds nothing. One frame is enough, and if the element
   * is still missing nothing happens, which is the right outcome for a field no tab renders.
   */
  const jumpToField = (field?: string | null) => {
    const target = tabForField(field);
    if (!target) {
      return;
    }
    setTab(target as TabId);
    requestAnimationFrame(() => {
      const input = document.getElementById(`field-${field}`);
      input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      input?.focus({ preventScroll: true });
    });
  };
  /**
   * Uncommitted edits, keyed by field name, saved as one `updateItem` call.
   *
   * Values are strings or string lists because the editable fields are both kinds — flattening a
   * list into a delimited string here would make any value containing the delimiter unrepresentable,
   * and provider cast names contain commas routinely.
   */
  const [draft, setDraft] = useState<Record<string, string | string[]>>({});
  /**
   * Locality overrides, as `field -> locality -> value`, kept apart from `draft` because the API
   * takes them as a nested structure rather than a flat field.
   */
  const [localityDraft, setLocalityDraft] = useState<
    Record<string, Record<string, string>>
  >({});

  const validations = useMemo<Validation[]>(
    () => item?.validations ?? [],
    [item],
  );
  const errorCount = validations.filter(
    (v) => v.level === ValidationLevel.Error,
  ).length;
  // Findings per tab, so each tab shows what it is hiding rather than the operator opening all of them.
  const tabCounts = useMemo(() => countByTab(validations), [validations]);
  const isDirty =
    Object.keys(draft).length > 0 || Object.keys(localityDraft).length > 0;

  useEffect(() => {
    if (!isDirty || updateItem.isPending) {
      return;
    }

    const timer = setTimeout(() => {
      void saveRef.current?.();
    }, AUTOSAVE_DELAY_MS);

    // Cleanup on every change is what makes this a debounce rather than a timer per edit: the pending
    // write is cancelled and rescheduled, so a burst of typing produces one save at the end of it.
    return () => clearTimeout(timer);
  }, [isDirty, draft, localityDraft, updateItem.isPending]);

  if (isLoading) {
    return <CircularProgress />;
  }
  if (error || !item) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Item not found'}
      </Alert>
    );
  }

  const scalar = (name: keyof ItemProperties) =>
    item.properties[name] as StringField | undefined;

  const changeField = (name: string, value: string) =>
    setDraft((prev) => ({ ...prev, [name]: value }));

  const changeListField = (name: string, values: string[]) =>
    setDraft((prev) => ({ ...prev, [name]: values }));

  const changeLocalityField = (name: string, locality: string, value: string) =>
    setLocalityDraft((prev) => ({
      ...prev,
      [name]: { ...(prev[name] ?? {}), [locality]: value },
    }));

  const revertField = (name: string) =>
    setDraft((prev) => {
      // Clearing to an empty string is how the API is told to drop the override — the same signal the
      // resolver already interprets, so revert needs no separate mutation here.
      const next = { ...prev };
      const persisted = scalar(name as keyof ItemProperties);
      if (persisted?.editorial) {
        next[name] = '';
      } else {
        delete next[name];
      }
      return next;
    });

  /**
   * The API replaces a field's **entire** per-locality map, so every territory has to be resent —
   * not just the edited one. Sending only the edit would silently delete the others, which is the
   * kind of data loss nobody notices until a territory stops being licensed.
   */
  const buildLocalityInput = () =>
    Object.fromEntries(
      Object.entries(localityDraft).map(([field, edits]) => {
        const persisted = Object.fromEntries(
          (
            item.properties[
              field as keyof ItemProperties
            ] as LocalityStringField
          ).variants
            .filter((v) => v.editorial != null)
            .map((v) => [v.locality, v.editorial as string]),
        );

        const merged = { ...persisted, ...edits };

        return [
          field,
          Object.entries(merged)
            // An emptied cell is a clear: drop it from the map rather than storing "".
            .filter(([, value]) => value.trim() !== '')
            .map(([locality, value]) => ({ locality, value })),
        ];
      }),
    );

  const save = async () => {
    try {
      const localityProperties = Object.keys(localityDraft).length
        ? buildLocalityInput()
        : undefined;

      const saved = await updateItem.mutateAsync({
        assetId: item.assetId,
        version: item.version,
        // The optimistic lock counter, taken from the last save rather than from the query cache.
        //
        // Every save increments it, and the mutation invalidates the item query, so there is a window
        // where the refetch has not landed and `item.editVersion` is one behind. Autosave writes often
        // enough to fall into that window regularly, and the symptom would be a spurious "modified in
        // another tab" error on the operator's own second edit.
        currentVersion: lastSavedVersion.current ?? item.editVersion,
        editorial: {
          ...draft,
          ...(localityProperties ? { localityProperties } : {}),
        } as EditorialInput,
      });
      lastSavedVersion.current = saved.editVersion;
      setDraft({});
      setLocalityDraft({});
      setSaveError(null);

      // Revalidate on every save, so findings track the field just edited.
      //
      // Rules run against saved state, so before this the panel showed results from before the edit — an
      // operator fixing a bad year watched the error persist and reasonably concluded the fix had not
      // worked. Asking them to press Revalidate to find out was the tool making its own bookkeeping their
      // problem, which is why that button is gone.
      //
      // Deliberately does NOT jump to the Validation tab the way the old button did: that made sense as a
      // response to a deliberate click and would be hostile in the middle of typing. The validation chip
      // is clickable for anyone who wants to look.
      await revalidate.mutateAsync({
        assetId: item.assetId,
        version: item.version,
      });
    } catch (e: unknown) {
      // Reported inline rather than by snackbar: there is no longer a click to respond to, a toast per
      // keystroke-pause would be noise, and a failure the operator must act on should not time out.
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  saveRef.current = save;

  const runDelete = async () => {
    try {
      await deleteItem.mutateAsync({
        assetId: item.assetId,
        version: item.version,
        currentVersion: item.editVersion,
      });
      setConfirmDelete(false);
      enqueueSnackbar('Removed from the catalogue — it can be restored', {
        variant: 'success',
      });
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Delete failed', {
        variant: 'error',
      });
    }
  };

  const runRestore = async () => {
    try {
      await restoreItem.mutateAsync({
        assetId: item.assetId,
        version: item.version,
        currentVersion: item.editVersion,
      });
      enqueueSnackbar('Restored to the catalogue', { variant: 'success' });
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Restore failed', {
        variant: 'error',
      });
    }
  };

  const runExport = async () => {
    try {
      const result = await exportItem.mutateAsync({
        assetId: item.assetId,
        version: item.version,
      });
      if (result.rejected.length) {
        enqueueSnackbar(result.rejected[0].reason, { variant: 'error' });
      } else {
        enqueueSnackbar('Export requested', { variant: 'success' });
      }
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Export failed', {
        variant: 'error',
      });
    }
  };

  const fieldProps = (name: keyof ItemProperties, label: string) => ({
    key: name,
    label,
    name,
    original: scalar(name)?.original,
    editorial: scalar(name)?.editorial,
    draft: draft[name] as string | undefined,
    onChange: changeField,
    onRevert: revertField,
    validations,
  });

  const listFieldProps = (
    name: string,
    label: string,
    field: { original?: string[] | null; editorial?: string[] | null },
  ) => ({
    key: name,
    label,
    name,
    original: field.original,
    editorial: field.editorial,
    draft: draft[name] as string[] | undefined,
    onChange: changeListField,
    validations,
  });

  return (
    <>
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
      >
        <Stack gap={0.5} minWidth={0}>
          <Typography variant="h5" noWrap>
            {scalar('title')?.editorial ?? scalar('title')?.original ?? assetId}
          </Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              {item.assetId} · {item.provider} · v{item.xmlVersion} · edit
              version {item.editVersion}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <StateChip state={item.state} />
          {/* Counts only the findings at the level the chip names. Passing the total read as
              "4 errors" on an item with one error and three warnings — the chip takes its wording from
              the worst level, so the number has to agree with it. Clicking goes to the findings, the
              same jump revalidate makes when it leaves errors behind. */}
          <ValidationChip
            level={item.worstValidationLevel}
            count={findingsAtWorstLevel(item.worstValidationLevel, validations)}
            onClick={() => setTab('validation')}
          />
          {/* Findings, state and the export banner all move without this page doing anything — an
              export completes seconds after it is requested, and a colleague can edit the same item.
              Scoped to this item plus its XML views, so refreshing a detail page does not re-fetch the
              whole catalogue behind it. */}
          <RefreshButton
            queryKeys={['item', 'xmlDiff', 'previewXml']}
            busy={updateItem.isPending || revalidate.isPending}
          />
          {/* No Save and no Revalidate button. Autosave writes every edit and revalidation follows each
              save, so both only ever asked the operator to request something that had already happened
              or was about to. This status line replaces them: automatic behaviour still has to be
              visible, or it is indistinguishable from edits going nowhere. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 128 }}
          >
            {updateItem.isPending
              ? 'Saving…'
              : revalidate.isPending
              ? 'Validating…'
              : isDirty
              ? 'Unsaved changes'
              : 'All changes saved'}
          </Typography>
          {/* The page's one filled gold button — publishing is what the operator is here to do. It was
              `color="secondary"`, which was the deep purple that reads as almost black on this
              background, so the most important action was also the least visible one. */}
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => void runExport()}
            // Export is blocked server-side on unresolved errors; disabling here explains why rather
            // than letting the operator click into a rejection.
            // Blocked on unresolved errors (also enforced server-side) and on a save still in flight,
            // so an export can never be built from a draft that has not landed. No longer gated on
            // isDirty: autosave clears that by itself, and waiting for it would leave the button dead
            // for a second after every keystroke.
            disabled={
              exportItem.isPending ||
              errorCount > 0 ||
              updateItem.isPending ||
              isDirty
            }
          >
            Export
          </Button>
          {item.deleted ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestoreFromTrashIcon />}
              onClick={() => void runRestore()}
              disabled={restoreItem.isPending}
            >
              Restore
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmDelete(true)}
              disabled={deleteItem.isPending}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Stack>

      {saveError && (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setSaveError(null)}
        >
          Could not save automatically: {saveError}
        </Alert>
      )}

      {item.deleted && (
        <Alert severity="warning" variant="outlined">
          This item has been removed from the catalogue. The provider’s original
          XML is kept and nothing already exported has been withdrawn — press
          Restore to put it back.
        </Alert>
      )}

      {errorCount > 0 && (
        <Alert severity="error">
          {errorCount} validation error{errorCount === 1 ? '' : 's'} must be
          resolved before this item can be exported.
          {/* Name the fields and make each one a way to get there. A count alone tells the operator that
              something is wrong and nothing about where — which is the whole complaint. */}
          <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {validations
              .filter((v) => v.level === ValidationLevel.Error)
              .map((v) => (
                <Chip
                  key={`${v.ruleId}-${v.field}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  label={v.field}
                  onClick={
                    tabForField(v.field)
                      ? () => jumpToField(v.field)
                      : undefined
                  }
                />
              ))}
          </Box>
        </Alert>
      )}
      {isDirty && (
        <Alert severity="info">
          Unsaved changes. Save before exporting so the emitted XML matches what
          you see.
        </Alert>
      )}
      {item.state === ItemState.ExportCompleted && item.exportKey && (
        <Alert severity="success">
          Exported {formatDateTime(item.exportedAt)} to{' '}
          <code>{item.exportKey}</code>
        </Alert>
      )}
      {item.error && <Alert severity="warning">{item.error}</Alert>}

      <Paper sx={{ minWidth: 0 }}>
        <Tabs
          value={tab}
          onChange={(_e, next: TabId) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            value="general"
            label={<TabLabel text="General" counts={tabCounts.general} />}
          />
          <Tab
            value="descriptions"
            label={
              <TabLabel text="Descriptions" counts={tabCounts.descriptions} />
            }
          />
          <Tab
            value="availability"
            label={
              <TabLabel text="Availability" counts={tabCounts.availability} />
            }
          />
          <Tab
            value="assets"
            label={<TabLabel text="Assets" counts={tabCounts.assets} />}
          />
          <Tab
            value="validation"
            label={`Validation (${validations.length})`}
          />
          <Tab value="xml" label="XML" />
        </Tabs>
        <Divider />

        <Box sx={{ p: 3, minWidth: 0 }}>
          {tab === 'general' && (
            <Stack gap={3}>
              <FieldGrid>
                {GENERAL.map((f) => (
                  <EditableField
                    {...fieldProps(f.name, f.label)}
                    options={
                      f.kind === 'vocabulary'
                        ? // `id`, not `name`: the id is what the data carries and what the `oneOf`
                          // vocabulary is built from, while the name is only a label. Passing the name as
                          // the value made every show type unselectable — `Series Episode` is not a
                          // configured value, `Series` is.
                          (f.vocabulary === 'showTypes'
                            ? showTypes
                            : audiences
                          )?.map((v) => ({ id: v.id, name: v.name }))
                        : f.kind === 'countries'
                        ? COUNTRIES.map((c) => ({
                            id: c.code,
                            name: `${c.code} · ${c.name}`,
                          }))
                        : undefined
                    }
                    constrain={
                      f.kind === 'constrained' ? f.constrain : undefined
                    }
                  />
                ))}
              </FieldGrid>
              <Divider />
              <FieldGrid>
                <EditableListField
                  {...listFieldProps(
                    'rating',
                    'Rating',
                    item.properties.rating,
                  )}
                  options={ratings?.map((r) => ({ id: r.id, name: r.name }))}
                />
                <EditableListField
                  {...listFieldProps('genre', 'Genre', item.properties.genre)}
                  options={genres?.map((g) => ({ id: g.id, name: g.name }))}
                />
                {/* A list, not a scalar: ADI `Languages` is plural, and every real package carrying a
                    single "es" today would break the moment one arrives as "es,en". No vocabulary yet —
                    the codes are ISO 639-1 and a curated list is VMS-49's remaining scope. */}
                <EditableListField
                  {...listFieldProps(
                    'languages',
                    'Languages',
                    item.properties.languages,
                  )}
                />
                <EditableListField
                  {...listFieldProps(
                    'category',
                    'Category',
                    item.properties.category.default,
                  )}
                  options={categories?.map((c) => ({
                    id: c.path,
                    name: c.path,
                  }))}
                />
                {/* No vocabulary: there is no closed set of cast or crew names. */}
                <EditableListField
                  {...listFieldProps(
                    'actors',
                    'Actors',
                    item.properties.actors,
                  )}
                />
                <EditableListField
                  {...listFieldProps(
                    'director',
                    'Director',
                    item.properties.director,
                  )}
                />
              </FieldGrid>
            </Stack>
          )}

          {tab === 'descriptions' && (
            <Stack gap={3}>
              <FieldGrid columns={1}>
                {DESCRIPTIONS.map((f) => (
                  <EditableField {...fieldProps(f.name, f.label)} multiline />
                ))}
              </FieldGrid>
              <Divider />
              <Typography variant="subtitle2">Series</Typography>
              <FieldGrid>
                {SERIES.map((f) => (
                  <EditableField {...fieldProps(f.name, f.label)} />
                ))}
              </FieldGrid>
            </Stack>
          )}

          {tab === 'availability' && (
            <Stack gap={3}>
              <FieldGrid>
                {AVAILABILITY.map((f) => (
                  <EditableField
                    {...fieldProps(f.name, f.label)}
                    constrain={
                      f.kind === 'constrained' ? f.constrain : undefined
                    }
                  />
                ))}
                <EditableListField
                  {...listFieldProps(
                    'availableInLocalities',
                    'Available in localities',
                    item.properties.availableInLocalities,
                  )}
                  // Offer ISO codes to pick from, and show a known operator code as its country. The
                  // stored value stays exactly what the provider sent.
                  options={COUNTRIES.map((c) => ({
                    id: c.code,
                    name: `${c.code} · ${c.name}`,
                  }))}
                  labelFor={localityLabel}
                />
                <EditableListField
                  {...listFieldProps(
                    'restrictedDeviceTypes',
                    'Restricted device types',
                    item.properties.restrictedDeviceTypes,
                  )}
                  options={deviceTypes?.map((d) => ({
                    id: d.id,
                    name: d.name,
                  }))}
                />
              </FieldGrid>
              <Divider />
              <Typography variant="subtitle2">
                Licensing and pricing, by locality
              </Typography>
              <LocalityTable
                item={item.properties}
                draft={draft}
                localityDraft={localityDraft}
                onChangeDefault={changeField}
                onChangeLocality={changeLocalityField}
              />
            </Stack>
          )}

          {tab === 'assets' && (
            <AssetsTable
              item={item.properties}
              media={item.media}
              assetId={item.assetId}
              version={item.version}
              currentVersion={item.editVersion}
              disabled={updateItem.isPending || isDirty}
            />
          )}

          {tab === 'validation' && (
            <ValidationTable
              validations={validations}
              onJumpToField={jumpToField}
            />
          )}

          {tab === 'xml' && <XmlTab assetId={assetId} version={version} />}
        </Box>
      </Paper>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Remove this item from the catalogue?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>
              {item.properties.title.editorial ??
                item.properties.title.original}
            </strong>{' '}
            will no longer appear in the catalogue or in search.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
            This is reversible. The provider’s original XML is kept and the item
            can be restored. Anything already exported stays where it was
            published.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void runDelete()}
            disabled={deleteItem.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary">
        Last updated {formatDateTime(item.updatedAt)}
        {item.updatedBy ? ` by ${item.updatedBy}` : ''}
      </Typography>
    </>
  );
};

const FieldGrid = ({
  children,
  columns = 3,
}: {
  children: React.ReactNode;
  columns?: number;
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2.5,
      gridTemplateColumns: {
        xs: '1fr',
        md: `repeat(${Math.min(columns, 2)}, 1fr)`,
        lg: `repeat(${columns}, 1fr)`,
      },
    }}
  >
    {children}
  </Box>
);

/**
 * Locality overrides are read-only in the PoC. They parse, merge and export correctly — proven by the
 * unit tests and the emitted XML — but the editor for them is Phase 2, and showing them read-only is
 * more useful than hiding the fact that the data exists.
 */
/** The four fields that can vary by territory, and the labels the editor shows for them. */
const LOCALITY_FIELDS = [
  {
    name: 'licensingWindowStart',
    label: 'Licensing window start',
    isDate: true,
  },
  { name: 'licensingWindowEnd', label: 'Licensing window end', isDate: true },
  { name: 'grossPrice', label: 'Gross price', isDate: false },
  { name: 'netPrice', label: 'Net price', isDate: false },
] as const;

/**
 * A licensing-window cell: a native date or datetime input, chosen from the value's own shape.
 *
 * Native rather than a picker library because `date` and `datetime-local` carry no timezone, so there is
 * no conversion to get wrong — a picker normalising to UTC would shift every window in the catalogue —
 * and because the bundle already warns at 1.08 MB.
 *
 * The shape is read from the value so that displaying a field cannot change it: a date-only value must
 * not gain a time, one with seconds must not lose them. A value matching no known shape stays a text
 * box, which is what keeps a corrupted one like `2024-04-01T0dff9:00:00` visible and fixable instead of
 * rendering as empty and then being saved as a cleared override. See dateShape.ts.
 */
const DateCell = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => {
  const shape = detectShape(value);
  const type = inputType(shape);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDateInput = type !== 'text';

  return (
    <TextField
      size="small"
      variant="standard"
      fullWidth
      type={type}
      placeholder="—"
      value={toInput(value, shape)}
      onChange={(e) => onChange(fromInput(e.target.value, value, shape))}
      inputRef={inputRef}
      // Ask for seconds so a value that has them stays editable rather than being truncated on focus.
      inputProps={type === 'datetime-local' ? { step: 1 } : undefined}
      // A date input has no placeholder, so the label has to carry the meaning; keep it shrunk so the
      // empty state does not look like a bug.
      InputLabelProps={type === 'text' ? undefined : { shrink: true }}
      InputProps={
        isDateInput
          ? {
              /**
               * Our own calendar button, replacing the browser's indicator.
               *
               * The native glyph is a fixed dark bitmap; inverting it got it visible but not legible, and
               * its shape barely reads as a calendar at this size. Hiding it (see ThemeProvider) and
               * drawing a real icon means it inherits the text colour like every other control, and it is
               * unmistakably a calendar.
               *
               * `showPicker()` is what opens the native panel — the reason for keeping a real date input
               * underneath rather than adopting a picker library, since that panel needs no timezone
               * handling of our own and no extra bundle.
               */
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Open the date picker">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Open the date picker"
                      onClick={() => inputRef.current?.showPicker?.()}
                      sx={{ color: 'text.primary' }}
                    >
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
};

/**
 * Licensing and pricing, editable per territory.
 *
 * The default column writes the plain scalar field; a locality column writes into
 * `localityProperties`. They are genuinely different fields in the API, not one field with a
 * qualifier, which is why the two cases are handled separately here rather than unified.
 *
 * A cell left empty clears the override and falls back to the default — the same "empty means use
 * the value underneath" rule the scalar editor follows.
 */
const LocalityTable = ({
  item,
  draft,
  localityDraft,
  onChangeDefault,
  onChangeLocality,
}: {
  item: ItemProperties;
  draft: Record<string, string | string[]>;
  localityDraft: Record<string, Record<string, string>>;
  onChangeDefault: (name: string, value: string) => void;
  onChangeLocality: (name: string, locality: string, value: string) => void;
}) => {
  const rows = LOCALITY_FIELDS.map((f) => ({
    ...f,
    field: item[f.name],
  }));

  const localities = Array.from(
    new Set([
      ...rows.flatMap((r) => r.field.variants.map((v) => v.locality)),
      ...(item.availableInLocalities.editorial ??
        item.availableInLocalities.original ??
        []),
    ]),
  ).sort();

  const cellValue = (name: string, locality: string) => {
    const pending = localityDraft[name]?.[locality];
    if (pending !== undefined) return pending;
    const variant = (
      item[name as keyof ItemProperties] as LocalityStringField
    ).variants.find((v) => v.locality === locality);
    return variant?.editorial ?? variant?.original ?? '';
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Default</TableCell>
          {localities.map((l) => (
            // `llapa` on its own means nothing to a reader. The label expands a known code to its
            // country; the value underneath is untouched, so the export still carries what the provider
            // sent. See countries.ts.
            <TableCell key={l} sx={{ whiteSpace: 'nowrap' }}>
              {localityLabel(l)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.name}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.label}</TableCell>
            <TableCell>
              {row.isDate ? (
                <DateCell
                  value={
                    (draft[row.name] as string | undefined) ??
                    row.field.default.editorial ??
                    row.field.default.original ??
                    ''
                  }
                  onChange={(next) => onChangeDefault(row.name, next)}
                />
              ) : (
                <TextField
                  size="small"
                  variant="standard"
                  fullWidth
                  placeholder="—"
                  value={
                    (draft[row.name] as string | undefined) ??
                    row.field.default.editorial ??
                    row.field.default.original ??
                    ''
                  }
                  onChange={(e) => onChangeDefault(row.name, e.target.value)}
                />
              )}
            </TableCell>
            {localities.map((l) => (
              <TableCell key={l}>
                {row.isDate ? (
                  <DateCell
                    value={cellValue(row.name, l)}
                    onChange={(next) => onChangeLocality(row.name, l, next)}
                  />
                ) : (
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="—"
                    value={cellValue(row.name, l)}
                    onChange={(e) =>
                      onChangeLocality(row.name, l, e.target.value)
                    }
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Artwork types an operator may replace, mirroring `REPLACEABLE_ASSET_TYPES` in the API.
 *
 * Duplicated rather than shared because the UI bundle and the Lambda have no common module, and the
 * API is the one that enforces it — this list only decides whether a button is offered.
 */
const REPLACEABLE_ASSET_TYPES = ['poster', 'box cover'];

const AssetsTable = ({
  item,
  media,
  assetId,
  version,
  currentVersion,
  disabled,
}: {
  item: ItemProperties;
  media: { filename: string; size: number; checksum: string }[];
  assetId: string;
  version: string;
  currentVersion: number;
  /** True while a save is in flight — an upload carries the same optimistic lock and would conflict. */
  disabled: boolean;
}) => {
  const replaceAsset = useReplaceAsset();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const replace = (type: string, file?: File | null) => {
    if (!file) {
      return;
    }
    setError(null);
    setPending(type);
    replaceAsset.mutate(
      { assetId, version, currentVersion, type, file },
      {
        onError: (e: unknown) =>
          setError(e instanceof Error ? e.message : 'Upload failed'),
        onSettled: () => setPending(null),
      },
    );
  };

  return (
    <Stack gap={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Preview</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>File</TableCell>
            <TableCell>Aspect ratio</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Delivered</TableCell>
            <TableCell>Size</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {item.assets.map((asset) => {
            const file = media.find((m) => m.filename === asset.content);
            const type = asset.type ?? '';
            const replaceable = REPLACEABLE_ASSET_TYPES.includes(type);
            return (
              <TableRow key={asset.id}>
                <TableCell>
                  <AssetThumbnail url={asset.url} alt={asset.content} />
                </TableCell>
                <TableCell>{asset.type ?? '—'}</TableCell>
                <TableCell>{asset.content}</TableCell>
                <TableCell>{asset.imageAspectRatio ?? '—'}</TableCell>
                <TableCell>
                  {asset.fromProvider ? 'Provider' : 'Replaced'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={asset.present ? 'success' : 'default'}
                    label={asset.present ? 'Yes' : 'No'}
                  />
                </TableCell>
                <TableCell>{file ? formatBytes(file.size) : '—'}</TableCell>
                <TableCell align="right">
                  {replaceable ? (
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      disabled={disabled || pending !== null}
                    >
                      {pending === type ? 'Uploading…' : 'Replace'}
                      <input
                        type="file"
                        hidden
                        // Artwork only, and the same list the API enforces — the picker narrows what
                        // can be chosen, the server decides what is allowed.
                        accept="image/jpeg,image/png"
                        onChange={(e) => {
                          replace(type, e.target.files?.[0]);
                          // Cleared so choosing the same file twice fires change again.
                          e.target.value = '';
                        }}
                      />
                    </Button>
                  ) : (
                    <Tooltip title="The video is delivered out of band and cannot be uploaded here">
                      <span>
                        <Button size="small" variant="outlined" disabled>
                          Replace
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Alert severity="info">
        The video is delivered out of band and never passes through VMS, so a
        movie asset shows as declared but not delivered. Only artwork travels
        inside the provider package — a replaced image is written into the
        exported ADI with its own size and checksum, and Revert on the item puts
        the provider&apos;s file back.
      </Alert>
    </Stack>
  );
};

/**
 * A tab label with its findings count.
 *
 * Errors and warnings are shown separately rather than as one total: an error blocks the export and a
 * warning does not, so collapsing them would hide the only distinction that changes what the operator has
 * to do. Red before amber, and nothing at all when the tab is clean.
 */
const TabLabel = ({
  text,
  counts,
}: {
  text: string;
  counts: { errors: number; warnings: number };
}) => (
  <Stack direction="row" gap={0.75} alignItems="center">
    <span>{text}</span>
    {counts.errors > 0 && (
      <Box
        component="span"
        aria-label={`${counts.errors} error${counts.errors === 1 ? '' : 's'}`}
        sx={{
          px: 0.75,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '18px',
          bgcolor: 'error.main',
          color: '#fff',
        }}
      >
        {counts.errors}
      </Box>
    )}
    {counts.warnings > 0 && (
      <Box
        component="span"
        aria-label={`${counts.warnings} warning${
          counts.warnings === 1 ? '' : 's'
        }`}
        sx={{
          px: 0.75,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '18px',
          bgcolor: 'warning.main',
          color: '#1a1005',
        }}
      >
        {counts.warnings}
      </Box>
    )}
  </Stack>
);

const ValidationTable = ({
  validations,
  onJumpToField,
}: {
  validations: Validation[];
  onJumpToField: (field?: string | null) => void;
}) =>
  validations.length === 0 ? (
    <Alert severity="success">
      No findings. This item satisfies every configured rule.
    </Alert>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Severity</TableCell>
          <TableCell>Field</TableCell>
          <TableCell>Rule</TableCell>
          <TableCell>Message</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {validations.map((v) => {
          const jumpable = !!tabForField(v.field);
          return (
            <TableRow
              key={`${v.ruleId}-${v.field}`}
              hover={jumpable}
              // The row is the target, not a separate "go to" button: the whole point is that finding the
              // field should take one click. Rows whose field no tab renders stay inert rather than
              // pretending to be clickable and doing nothing.
              onClick={jumpable ? () => onJumpToField(v.field) : undefined}
              sx={jumpable ? { cursor: 'pointer' } : undefined}
            >
              <TableCell>
                <ValidationChip level={v.level} count={1} />
              </TableCell>
              <TableCell>
                {v.field}
                {jumpable && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Click to fix
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <code>{v.ruleId}</code>
              </TableCell>
              <TableCell>{v.message}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

export default ItemDetail;
